import { CheckSquare } from 'lucide-react';
import { PageHeader } from '@/shared/components/ui/PageHeader';
// Admin/Superadmin queue: edits requested against an already-finalized
// Invoice or Tax Invoice, or an amount change to a Sales Order already handed
// off to Dispatch. Nothing here changed until this page approves it — see the
// gate in src/api-sales/orders.js and src/api-accounts/sales-invoices.js.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Check, X, ShieldAlert } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Spinner } from '@/shared/components/ui/Spinner';
import { formatINR } from '@/shared/utils/currency';
import { useAuthStore } from '@/shared/stores/auth.store';
import {
  useInvoiceEditRequests, useInvoiceEditRequestActions, type InvoiceEditRequest,
} from '../hooks/useInvoiceEditRequests';

const LABEL: Record<InvoiceEditRequest['documentType'], string> = {
  direct_sale: 'Invoice',
  tax_invoice: 'Tax Invoice',
  sales_order: 'Sales Order',
};

interface NormalizedLine {
  description: string;
  quantity: number;
  rate: number;
  discount: number;
  taxRate: number;
  lineTotal: number;
}

/**
 * direct_sale and sales_order (both SalesOrderModel) lines use unitPrice +
 * discount/discountType (a percent OR a rupee amount, per the flag);
 * tax_invoice (AccountsSalesInvoiceModel) lines use rate + a plain rupee
 * discount. Both normalise to the same shape so one diff renderer below can
 * compare either document type without caring which one it's looking at.
 */
function normalizeLine(l: Record<string, unknown>, documentType: InvoiceEditRequest['documentType']): NormalizedLine {
  const quantity = Number(l.quantity) || 0;
  const rate = Number(documentType === 'tax_invoice' ? l.rate : l.unitPrice) || 0;
  const rawDiscount = Number(l.discount) || 0;
  const discount = documentType !== 'tax_invoice' && l.discountType === 'percent'
    ? Math.round(quantity * rate * (rawDiscount / 100) * 100) / 100
    : rawDiscount;
  const taxRate = (Number(l.cgstRate) || 0) + (Number(l.sgstRate) || 0) + (Number(l.igstRate) || 0);
  return {
    description: String(l.description || ''), quantity, rate, discount, taxRate,
    lineTotal: Math.round((quantity * rate - discount) * 100) / 100,
  };
}

/** One row per line position, before and after aligned by index — the
 *  common case (editing an existing line) lines up correctly; an added or
 *  removed line shows as one side being undefined past the shorter array. */
function buildLineDiff(req: InvoiceEditRequest): Array<{ before?: NormalizedLine; after?: NormalizedLine }> {
  // A hand-off edit changes transport/payment/dates only — its lines are untouched.
  if (req.computedResult?.dispatchHandoff) return [];
  const before = (req.previousLines ?? []).map((l) => normalizeLine(l, req.documentType));
  const afterRaw = req.computedResult?.lineItems ?? req.computedResult?.lines ?? [];
  const after = afterRaw.map((l) => normalizeLine(l, req.documentType));
  const rows: Array<{ before?: NormalizedLine; after?: NormalizedLine }> = [];
  for (let i = 0; i < Math.max(before.length, after.length); i++) {
    rows.push({ before: before[i], after: after[i] });
  }
  return rows;
}

const money = (n: number) => formatINR(n);

function DiffCell({ before, after, format = (n: number) => String(n) }: { before?: number; after?: number; format?: (n: number) => string }) {
  if (before === undefined || after === undefined || before === after) {
    return <span>{format(after ?? before ?? 0)}</span>;
  }
  return (
    <span className="flex flex-col leading-tight">
      <span className="text-on-surface-variant line-through">{format(before)}</span>
      <span className="font-semibold text-primary">{format(after)}</span>
    </span>
  );
}

function LineItemDiff({ rows }: { rows: Array<{ before?: NormalizedLine; after?: NormalizedLine }> }) {
  if (rows.length === 0) return null;
  return (
    <div className="mt-3 overflow-x-auto rounded-xl border border-outline/60">
      <table className="w-full min-w-[520px] text-left text-body-xs">
        <thead className="bg-surface-variant/50 text-on-surface-variant">
          <tr>
            <th className="px-3 py-2 font-semibold">Item</th>
            <th className="px-3 py-2 font-semibold">Qty</th>
            <th className="px-3 py-2 font-semibold">Rate</th>
            <th className="px-3 py-2 font-semibold">Disc</th>
            <th className="px-3 py-2 font-semibold">Tax %</th>
            <th className="px-3 py-2 text-right font-semibold">Line total</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-outline/40">
          {rows.map((row, i) => {
            const added = !row.before && row.after;
            const removed = row.before && !row.after;
            const label = row.after?.description || row.before?.description || '(unnamed item)';
            return (
              <tr key={i} className={removed ? 'bg-danger/10/60' : added ? 'bg-success/10/60' : undefined}>
                <td className="px-3 py-2">
                  {label}
                  {added && <span className="ml-1.5 rounded-full bg-success/10 px-1.5 py-0.5 text-data-xs font-bold text-success-text">NEW</span>}
                  {removed && <span className="ml-1.5 rounded-full bg-danger/10 px-1.5 py-0.5 text-data-xs font-bold text-danger-text">REMOVED</span>}
                </td>
                <td className="px-3 py-2"><DiffCell before={row.before?.quantity} after={row.after?.quantity} /></td>
                <td className="px-3 py-2"><DiffCell before={row.before?.rate} after={row.after?.rate} format={money} /></td>
                <td className="px-3 py-2"><DiffCell before={row.before?.discount} after={row.after?.discount} format={money} /></td>
                <td className="px-3 py-2"><DiffCell before={row.before?.taxRate} after={row.after?.taxRate} format={(n) => `${n}%`} /></td>
                <td className="px-3 py-2 text-right font-semibold"><DiffCell before={row.before?.lineTotal} after={row.after?.lineTotal} format={money} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const TRANSPORT_LABEL: Record<string, string> = { in_house_driver: 'In-house driver', third_party_courier: 'Third-party courier / transporter', local_transport: 'Local transport' };

type DocRef = { url: string; name: string };

/**
 * Which payment-proof documents the edit removes and which it adds — the
 * usual reason for a hand-off edit is "wrong screenshot/PDF attached", and the
 * approver has to be able to open both before saying yes.
 */
function HandoffDocumentsDiff({ before, after }: { before?: DocRef[]; after?: DocRef[] }) {
  if (!before && !after) return null;
  const was = new Set((before || []).map((d) => d.url));
  const now = new Set((after || []).map((d) => d.url));
  const removed = (before || []).filter((d) => !now.has(d.url));
  const added = (after || []).filter((d) => !was.has(d.url));
  const kept = (after || []).filter((d) => was.has(d.url));
  if (!removed.length && !added.length) {
    return <p className="mt-2 text-body-xs text-on-surface-variant">Payment proof documents: unchanged ({kept.length}).</p>;
  }
  const link = (d: DocRef) => (
    <a href={d.url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-primary">{d.name || 'document'}</a>
  );
  return (
    <div className="mt-2 rounded-lg border border-warning/30 bg-warning/10/70 p-2.5 text-body-xs">
      <p className="font-semibold text-warning-text">Payment proof documents change — open both before approving</p>
      {removed.map((d) => <p key={'r' + d.url} className="mt-1 text-danger-text">Removed: {link(d)}</p>)}
      {added.map((d) => <p key={'a' + d.url} className="mt-1 text-success-text">Added: {link(d)}</p>)}
      {kept.length > 0 && <p className="mt-1 text-on-surface-variant">Unchanged: {kept.map((d, i) => <span key={d.url}>{i ? ', ' : ''}{link(d)}</span>)}</p>}
    </div>
  );
}

/** What a Dispatch hand-off edit will set, once approved. */
function HandoffSummary({ handoff }: { handoff: NonNullable<InvoiceEditRequest['computedResult']['dispatchHandoff']> }) {
  const rows: Array<[string, string]> = [
    ['Payment', handoff.paymentArrangement || '—'],
    ['Transport', [TRANSPORT_LABEL[handoff.transportMode || ''] || handoff.transportMode, handoff.courierName || handoff.driverName].filter(Boolean).join(' · ') || '—'],
    ['Dispatch due', handoff.requiredDispatchDate ? new Date(handoff.requiredDispatchDate).toLocaleDateString('en-IN') : '—'],
    ['Note for Dispatch', handoff.note || '—'],
  ];
  return (
    <div className="mt-3 rounded-xl border border-outline/60 p-3">
      <p className="text-body-xs font-semibold text-on-surface">Dispatch hand-off after this change</p>
      <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-body-xs">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-on-surface-variant">{k}</dt>
            <dd className="text-on-surface">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function RequestCard({ req }: { req: InvoiceEditRequest }) {
  const { approve, reject } = useInvoiceEditRequestActions();
  const [note, setNote] = useState('');
  const [showReject, setShowReject] = useState(false);
  const deltaUp = req.proposedGrandTotal >= req.previousGrandTotal;
  const diffRows = buildLineDiff(req);
  const added = diffRows.filter((r) => !r.before && r.after).length;
  const removed = diffRows.filter((r) => r.before && !r.after).length;
  const changed = diffRows.filter((r) => r.before && r.after
    && (r.before.quantity !== r.after.quantity || r.before.rate !== r.after.rate || r.before.discount !== r.after.discount || r.before.taxRate !== r.after.taxRate)).length;

  const onApprove = () => {
    if (!note.trim()) { toast.error('Add a short reason for approving this change.'); return; }
    approve.mutate({ id: req._id, note }, {
      onSuccess: () => toast.success(`${LABEL[req.documentType]} ${req.documentNumber} updated.`),
      onError: (err: unknown) => {
        const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        toast.error(message || 'Could not approve this request.');
      },
    });
  };

  const onReject = () => {
    if (!note.trim()) { toast.error('Add a short note explaining why this is rejected.'); return; }
    reject.mutate({ id: req._id, note }, {
      onSuccess: () => toast.success('Request rejected. The invoice is unchanged.'),
      onError: (err: unknown) => {
        const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        toast.error(message || 'Could not reject this request.');
      },
    });
  };

  return (
    <div className="rounded-card border border-border bg-card p-5 shadow-xs">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-body-xs font-semibold text-primary">
              {LABEL[req.documentType]}
            </span>
            <span className="font-semibold text-on-surface">{req.documentNumber || '(no number)'}</span>
            <span className="rounded-md bg-amber-100 text-amber-900 border border-amber-300 px-2 py-0.5 text-body-xs font-bold">
              Revision #{req.revisionCount || 1}
            </span>
          </div>
          <p className="mt-1 text-body-xs text-on-surface-variant">
            Requested by <strong>{req.requestedByName || 'a user'}</strong> · {new Date(req.requestedAt).toLocaleString('en-IN')}
          </p>
        </div>
        <div className="text-right">
          <p className="text-body-xs text-on-surface-variant">
            {formatINR(req.previousGrandTotal)} → <span className={deltaUp ? 'text-success-text' : 'text-danger-text'}>{formatINR(req.proposedGrandTotal)}</span>
          </p>
          <p className="text-body-xs text-on-surface-variant">
            {[
              changed > 0 && `${changed} line${changed === 1 ? '' : 's'} changed`,
              added > 0 && `${added} added`,
              removed > 0 && `${removed} removed`,
            ].filter(Boolean).join(', ') || 'No line changes'}
          </p>
        </div>
      </div>

      <div className="mt-3 rounded-xl bg-amber-50/70 border border-amber-200/80 p-3.5">
        <p className="text-body-xs font-bold text-amber-900 uppercase tracking-wider">BDM's Reason for Request</p>
        <p className="mt-1 text-body-sm font-medium text-amber-950">{req.reason || req.bdmReason || 'No reason specified by BDM.'}</p>
      </div>

      {req.computedResult?.patch?.billToName !== undefined && (
        <div className="mt-3 rounded-xl border border-warning/30 bg-warning/10 p-3 text-body-sm text-warning-text">
          <p className="text-body-xs font-semibold">New Bill To</p>
          <p className="mt-0.5 font-semibold">{req.computedResult.patch.billToName}</p>
          <p>{[req.computedResult.patch.billingAddress?.street, req.computedResult.patch.billingAddress?.city, req.computedResult.patch.billingAddress?.state, req.computedResult.patch.billingAddress?.zipCode, req.computedResult.patch.billingAddress?.country].filter(Boolean).join(', ')}</p>
          <p>GSTIN: {req.computedResult.patch.billToGstin || '—'}</p>
        </div>
      )}
      {req.computedResult?.patch?.foreignCurrency !== undefined && (
        <div className="mt-3 rounded-xl border border-warning/30 bg-warning/10 p-3 text-body-sm text-warning-text">
          <p className="text-body-xs font-semibold">New billing currency</p>
          <p className="mt-0.5">{req.computedResult.patch.foreignCurrency
            ? `${req.computedResult.patch.foreignCurrency} at 1 ${req.computedResult.patch.foreignCurrency} = ₹${req.computedResult.patch.foreignRate}`
            : 'INR'}</p>
        </div>
      )}
      {req.computedResult?.dispatchHandoff && <HandoffSummary handoff={req.computedResult.dispatchHandoff} />}
      {req.computedResult?.dispatchHandoff && (
        <HandoffDocumentsDiff before={req.computedResult.previousPaymentProofs} after={req.computedResult.dispatchHandoff.paymentProofs} />
      )}

      <LineItemDiff rows={diffRows} />

      {/* A reason is required for the decision either way (client, 2026-10-05). */}
      <label className="mt-3 flex flex-col gap-1.5">
        <span className="text-body-sm font-medium text-on-surface">
          {showReject ? 'Reason for rejecting' : 'Reason for your decision'} <span className="text-danger-text">*</span>
        </span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          className="rounded-xl border border-outline bg-surface px-3 py-2 text-body-sm focus:border-primary focus:outline-none"
          placeholder={showReject ? 'Why is this being rejected?' : 'Why is this change approved (or rejected)?'}
        />
      </label>

      <div className="mt-4 flex items-center justify-end gap-2">
        {!showReject ? (
          <Button variant="outline" onClick={() => setShowReject(true)}>
            <X className="mr-1.5 h-4 w-4" /> Reject
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={() => setShowReject(false)}>Cancel</Button>
            <Button variant="outline" disabled={reject.isPending || !note.trim()} onClick={onReject}>
              {reject.isPending ? 'Rejecting…' : 'Confirm reject'}
            </Button>
          </>
        )}
        {!showReject && (
          <Button disabled={approve.isPending || !note.trim()} onClick={onApprove}>
            <Check className="mr-1.5 h-4 w-4" /> {approve.isPending ? 'Approving…' : 'Approve & apply'}
          </Button>
        )}
      </div>
    </div>
  );
}

export function InvoiceEditApprovalsPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const { data: pending = [], isLoading } = useInvoiceEditRequests('pending');
  const { data: decided = [] } = useInvoiceEditRequests();

  const history = decided.filter((r) => r.status !== 'pending').slice(0, 20);

  if (!isAdmin) {
    return (
      <div className="flex w-full flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
        <ShieldAlert className="h-8 w-8 text-warning-text" />
        <p className="max-w-md text-body-sm text-on-surface">
          Only an Admin or Superadmin can review invoice edit requests.
        </p>
        <Button variant="outline" onClick={() => navigate(-1)}>Back</Button>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-1 flex-col gap-6 pb-16">
      <header className="flex items-center gap-3 border-b border-outline/60 pb-5">
        <Button variant="outline" size="icon" onClick={() => navigate(-1)} aria-label="Back" className="h-10 w-10 rounded-xl">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <PageHeader icon={CheckSquare} eyebrow="Finance" title="Invoice Edit Approvals" description={<>Changes requested against a finalized Invoice or Tax Invoice — nothing here has been applied yet.</>} />
      </header>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center p-8"><Spinner /></div>
      ) : pending.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-outline p-10 text-center text-body-sm text-on-surface-variant">
          Nothing pending. Requests appear here the moment someone edits a finalized invoice.
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {pending.map((req) => <RequestCard key={req._id} req={req} />)}
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-2">
          <h2 className="mb-3 text-body-base font-semibold text-on-surface">Recently decided</h2>
          <div className="overflow-x-auto rounded-2xl border border-outline">
            <table className="w-full min-w-[760px] text-left text-body-sm">
              <thead className="bg-surface-variant/60 text-body-xs uppercase text-on-surface-variant">
                <tr>
                  <th className="px-4 py-2">Document</th>
                  <th className="px-4 py-2">Revision</th>
                  <th className="px-4 py-2">Requested by</th>
                  <th className="px-4 py-2">BDM Request Reason</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Decided by</th>
                  <th className="px-4 py-2">Decision Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-outline">
                {history.map((r) => (
                  <tr key={r._id}>
                    <td className="px-4 py-2 font-medium">{LABEL[r.documentType]} {r.documentNumber}</td>
                    <td className="px-4 py-2">
                      <span className="rounded bg-surface-variant px-1.5 py-0.5 text-body-xs font-bold font-mono">
                        #{r.revisionCount || 1}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-on-surface-variant">{r.requestedByName}</td>
                    <td className="px-4 py-2 max-w-xs text-body-xs text-on-surface font-medium">{r.reason || r.bdmReason || '—'}</td>
                    <td className="px-4 py-2">
                      <span className={r.status === 'approved' ? 'text-success-text font-semibold' : 'text-danger-text font-semibold'}>
                        {r.status === 'approved' ? 'Approved' : 'Rejected'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-on-surface-variant">{r.reviewedByName || '—'}</td>
                    <td className="px-4 py-2 max-w-xs text-body-xs text-on-surface-variant">{r.reviewNote || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default InvoiceEditApprovalsPage;
